<template>
    <v-container fluid>
        <v-row>
            <v-col>
                <SellProductFiltersComponent
                    ref="sell-product-search"
                    :showShops="true"
                    :enableFieldType="false"
                    :showSingleArticleFilter="false"
                    preselectFamily
                    @searchPressed="getEBITDAAnalysis"
                    @changeFieldType="
                        (f) => {
                            fieldType = f;
                        }
                    "
                ></SellProductFiltersComponent>
            </v-col>
        </v-row>

        <!-- EBITDA Summary Cards -->
        <v-row dense v-if="!loading && summary">
            <v-col cols="12" md="8">
                <v-card>
                    <v-card-title class="text-h6">
                        Analisi EBITDA - Efficienza Margine per cm²
                    </v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col cols="12" md="4" v-for="(stats, classType) in summary.ebitda_distribution" :key="classType">
                                <div class="text-center">
                                    <v-chip large :color="getEBITDAColor(classType)" dark class="ma-2">
                                        Classe {{ classType }}
                                    </v-chip>
                                    <div class="mt-2">
                                        <div><strong>{{ stats.count }}</strong> prodotti</div>
                                        <div><small>{{ stats.percentage.toFixed(1) }}% del totale</small></div>
                                        <div class="mt-1">
                                            <strong>Margine:</strong> {{ stats.total_margin | formatEuro }}
                                        </div>
                                        <div>
                                            <strong>Spazio:</strong> {{ stats.total_space | roundSecondDecimal }} cm²
                                        </div>
                                        <div>
                                            <strong>Euro/cm²:</strong> {{ stats.avg_euro_cm2 | formatEuro }}
                                        </div>
                                    </div>
                                </div>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="12" md="4">
                <v-card>
                    <v-card-title class="text-h6">
                        Indicatori Chiave
                    </v-card-title>
                    <v-card-text>
                        <div><strong>Totale prodotti:</strong> {{ summary.total_products }}</div>
                        <div><strong>Margine totale:</strong> {{ summary.margin_analysis.total_margin | formatEuro }}</div>
                        <div><strong>Spazio totale:</strong> {{ summary.margin_analysis.total_space | roundSecondDecimal }} cm²</div>
                        <div><strong>EBITDA medio:</strong> {{ summary.margin_analysis.avg_euro_cm2 | formatEuro }}/cm²</div>
                        <div class="mt-2">
                            <v-chip small color="success" dark>
                                <strong>{{ summary.margin_analysis.margin_efficiency_leaders }}</strong> prodotti ad alta efficienza
                            </v-chip>
                        </div>
                        <div class="mt-1">
                            <v-chip small color="error" dark>
                                <strong>{{ summary.margin_analysis.margin_efficiency_underperformers }}</strong> prodotti a bassa efficienza
                            </v-chip>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Data Table -->
        <v-row dense>
            <v-col>
                <div v-if="!loading">
                    <v-data-table
                        dense
                        multi-sort
                        fixed-header
                        :search="search"
                        :item-class="rowColorClass"
                        :height="600"
                        :headers="headers"
                        :items="filteredData"
                        :options="{
                            sortBy: ['ebitda_class', 'euro_cm2'],
                            sortDesc: [false, true]
                        }"
                        :items-per-page="100"
                        :footer-props="{
                            'items-per-page-options': [20, 50, 100, 200, -1],
                        }"
                    >
                        <template v-slot:top>
                            <v-toolbar flat>
                                <v-text-field
                                    dense
                                    v-model="search"
                                    label="Cerca..."
                                    class="mx-2"
                                    style="max-width: 200px;"
                                >
                                </v-text-field>
                                <v-select
                                    dense
                                    v-model="ebitdaFilter"
                                    :items="ebitdaFilterOptions"
                                    label="Filtro EBITDA"
                                    class="mx-2"
                                    style="max-width: 150px;"
                                    @change="applyFilters"
                                >
                                </v-select>
                                <v-spacer></v-spacer>
                                <v-btn
                                    color="success"
                                    small
                                    dark
                                    class="mb-2"
                                    @click="exportEBITDAAnalysis()"
                                >
                                    Esporta Analisi EBITDA
                                    <v-icon small>assessment</v-icon>
                                </v-btn>
                            </v-toolbar>
                        </template>
                        <template v-slot:item.cod_articolo="{ item }">
                            <router-link
                                target="_blank"
                                :to="{
                                    name: 'product',
                                    query: {
                                        id: item.cod_articolo,
                                    },
                                }"
                            >
                                {{ item.cod_articolo }}
                            </router-link>
                        </template>
                        <template v-slot:item.ebitda_class="{ item }">
                            <v-chip small :color="getEBITDAColor(item.ebitda_class)" dark>
                                {{ item.ebitda_class }}
                            </v-chip>
                        </template>
                        <template v-slot:item.prezzo_vendita="{ item }">
                            {{ item.prezzo_vendita | formatEuro }}
                        </template>
                        <template v-slot:item.prezzo_acquisto="{ item }">
                            {{ item.prezzo_acquisto | formatEuro }}
                        </template>
                        <template v-slot:item.margine_totale="{ item }">
                            {{ item.margine_totale | formatEuro }}
                        </template>
                        <template v-slot:item.euro_cm2="{ item }">
                            {{ item.euro_cm2 | formatEuro }}
                        </template>
                        <template v-slot:item.spazio="{ item }">
                            {{ item.spazio | roundSecondDecimal }}
                        </template>
                        <template v-slot:item.velocita="{ item }">
                            {{ item.velocita | roundSecondDecimal }}
                        </template>
                        <template v-slot:item.saturazione="{ item }">
                            {{ item.saturazione | formatPercent }}
                        </template>
                    </v-data-table>
                </div>
                <div v-else style="text-align: center">
                    <v-progress-circular
                        indeterminate
                        color="primary"
                    ></v-progress-circular>
                </div>
            </v-col>
        </v-row>
    </v-container>
</template>

<style scoped lang="scss"></style>

<script>
import requests from "../../requests";
import SellProductFiltersComponent from "../../components/SellProductFiltersComponent.vue";
import $ from "jquery";

const headers = [
    { text: "Codice Articolo", value: "cod_articolo" },
    { text: "Descrizione", value: "ar_descr", width: 250 },
    { text: "EBITDA", value: "ebitda_class", align: "center", filterable: false },
    { text: "Euro/cm²", value: "euro_cm2", align: "end", filterable: false },
    { text: "Margine Totale", value: "margine_totale", align: "end", filterable: false },
    { text: "Spazio(cm²)", value: "spazio", align: "end", filterable: false },
    { text: "Quantità", value: "qty_veduta", align: "end", filterable: false },
    { text: "Prezzo Vendita", value: "prezzo_vendita", align: "end", filterable: false },
    { text: "Prezzo Acquisto", value: "prezzo_acquisto", align: "end", filterable: false },
    { text: "Velocità(pz/gg)", value: "velocita", align: "end", filterable: false },
    { text: "Saturazione", value: "saturazione", align: "end", filterable: false },
    { text: "Famiglia", value: "famiglia", width: 200 },
    { text: "Sottofamiglia", value: "sottofamiglia", width: 200 },
    { text: "Ultima Vendita", value: "data_ultima_vendita", filterable: false },
];

export default {
    name: "product-ebitda-analysis",
    components: { SellProductFiltersComponent },
    computed: {
        headers() {
            return headers;
        },
    },
    data: () => {
        return {
            loading: false,
            data: [],
            search: "",
            fieldType: "prezzo",
            ebitdaFilter: "all",
            ebitdaFilterOptions: [
                { text: "Tutti", value: "all" },
                { text: "A (Alta Efficienza)", value: "A" },
                { text: "B (Media Efficienza)", value: "B" },
                { text: "C (Bassa Efficienza)", value: "C" }
            ],
            filteredData: [],
            summary: null,
        };
    },

    mounted() {
        console.log("Product EBITDA Analysis Mounted.");
        let filters = {};
        this.getEBITDAAnalysis(filters);
    },

    methods: {
        getEBITDAAnalysis(filters) {
            console.log(filters);
            this.loading = true;
            requests
                .getProductsEBITDA(filters)
                .then((res) => {
                    this.data = res.data.products || [];
                    this.summary = res.data.summary || null;
                    this.applyFilters();
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        rowColorClass(row) {
            switch (row.ebitda_class) {
                case 'A': return "row-color-excellent";
                case 'B': return "row-color-very-good";
                case 'C': return "row-color-poor";
                default: return "";
            }
        },
        getEBITDAColor(ebitdaClass) {
            switch (ebitdaClass) {
                case 'A': return 'success';
                case 'B': return 'warning';
                case 'C': return 'error';
                default: return 'grey';
            }
        },
        exportEBITDAAnalysis() {
            let filters = this.$refs["sell-product-search"].getFilters();
            if (!filters.shop_id) {
                filters.shop_id = 7;
            }
            window.open(
                "/api/products/ebitda?" + $.param(filters),
                "_blank"
            );
        },
        applyFilters() {
            let filtered = this.data;

            // Apply EBITDA filter
            if (this.ebitdaFilter !== "all") {
                filtered = filtered.filter(item => item.ebitda_class === this.ebitdaFilter);
            }

            this.filteredData = filtered;
        }
    },

    filters: {
        roundSecondDecimal(value) {
            return Math.round(value * 100) / 100;
        },

        formatEuro(number) {
            if (_.isNaN(number)) return 0;
            return new Intl.NumberFormat("it-IT", {
                style: "currency",
                currency: "EUR",
            }).format(number);
        },
        formatPercent(value) {
            return value.toFixed(2) + "%";
        },
    },
};
</script>

<style lang="scss">
.row-color-excellent {
    background-color: rgba($color: #4CAF50, $alpha: 0.3) !important;
}
.row-color-very-good {
    background-color: rgba($color: #8BC34A, $alpha: 0.3) !important;
}
.row-color-poor {
    background-color: rgba($color: #F44336, $alpha: 0.3) !important;
}
</style>